/**
 * Estadística del apartado Análisis del repositorio de Redes Sociales: cuantiles, dispersión,
 * correlación, agrupación por marca/cliente, comparativas, mapa de calor día × hora y
 * frecuencia de palabras y hashtags. Lógica pura (sin React) para poder probarla.
 */
import { num, profileKey } from './socialRepoUtils';

/* ---------- Estadística básica ---------- */

export function mean(values) {
    return values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
}

export function stdDev(values) {
    if (values.length < 2) return null;
    const m = mean(values);
    return Math.sqrt(values.reduce((acc, v) => acc + (v - m) ** 2, 0) / (values.length - 1));
}

/** Cuantil con interpolación lineal (como Excel PERCENTIL.INC). */
export function quantile(sortedValues, q) {
    if (!sortedValues.length) return null;
    const pos = (sortedValues.length - 1) * q;
    const lo = Math.floor(pos);
    const hi = Math.ceil(pos);
    return sortedValues[lo] + (sortedValues[hi] - sortedValues[lo]) * (pos - lo);
}

/** Resumen de cinco números, media, desviación, coeficiente de variación y atípicos altos. */
export function describe(values) {
    const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
    if (!sorted.length) return null;
    const q1 = quantile(sorted, 0.25);
    const q3 = quantile(sorted, 0.75);
    const m = mean(sorted);
    const sd = stdDev(sorted);
    const upperFence = q3 + 1.5 * (q3 - q1);
    return {
        n: sorted.length,
        min: sorted[0],
        q1,
        median: quantile(sorted, 0.5),
        q3,
        max: sorted[sorted.length - 1],
        mean: m,
        sd,
        cv: sd != null && m ? sd / m : null,
        upperFence,
        outliers: sorted.filter(v => v > upperFence).length,
    };
}

/** Correlación de Pearson; null si alguna serie no varía o hay menos de 3 pares. */
export function pearson(xs, ys) {
    const pairs = xs.map((x, i) => [x, ys[i]]).filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y));
    if (pairs.length < 3) return null;
    const mx = mean(pairs.map(p => p[0]));
    const my = mean(pairs.map(p => p[1]));
    let sxy = 0;
    let sxx = 0;
    let syy = 0;
    pairs.forEach(([x, y]) => {
        sxy += (x - mx) * (y - my);
        sxx += (x - mx) ** 2;
        syy += (y - my) ** 2;
    });
    if (!sxx || !syy) return null;
    return sxy / Math.sqrt(sxx * syy);
}

/** Matriz de correlación entre métricas (solo filas con valor en ambas). */
export function correlationMatrix(rows, metrics) {
    return metrics.map(a => metrics.map((b) => {
        if (a === b) return 1;
        const subset = rows.filter(row => row[a] !== '' && row[a] != null && row[b] !== '' && row[b] != null);
        return pearson(subset.map(r => num(r[a])), subset.map(r => num(r[b])));
    }));
}

/* ---------- Marca / cliente ---------- */

const normalizeText = (text) => String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/**
 * Llave de marca de un perfil: quita el @usuario entre paréntesis, la arroba, «México»,
 * artículos iniciales y signos, para unir «Bafar», «Bafar México» y «Bafar (bafar_mexico)».
 */
export function brandKey(profileName) {
    let text = normalizeText(profileName)
        .replace(/\(.*?\)/g, ' ')
        .replace(/^@/, '')
        .replace(/\b(mexico|mx)\b/g, ' ')
        .replace(/^\s*(el|la|los|las)\s+/, '')
        .replace(/[^a-z0-9]+/g, '');
    if (text.endsWith('mexico') && text.length > 8) text = text.slice(0, -6);
    return text || normalizeText(profileName).replace(/[^a-z0-9]+/g, '');
}

/**
 * Cliente de cada perfil: el cliente del sistema cuyo nombre coincide con la marca, o la
 * marca misma con el nombre más corto visto. Map<perfilKey, nombre>.
 */
export function clientGrouping(profiles, systemClients = []) {
    const clientKeys = systemClients
        .map(c => ({ name: c.nombre || c.name || '', key: brandKey(c.nombre || c.name || '') }))
        .filter(c => c.key.length >= 3);
    const brandNames = new Map();
    profiles.forEach(({ perfil }) => {
        const key = brandKey(perfil);
        const clean = String(perfil).replace(/\s*\(.*?\)\s*/g, ' ').replace(/^@/, '').trim();
        const prev = brandNames.get(key);
        /* Se prefiere un nombre «de marca» (con mayúsculas) y, entre esos, el más corto. */
        const styled = (name) => (name !== name.toLowerCase() ? 0 : 1);
        if (!prev || styled(clean) < styled(prev) || (styled(clean) === styled(prev) && clean.length < prev.length)) {
            brandNames.set(key, clean);
        }
    });
    const out = new Map();
    profiles.forEach((p) => {
        const key = brandKey(p.perfil);
        const match = clientKeys.find(c => key === c.key || key.includes(c.key) || c.key.includes(key));
        out.set(p.key, match ? match.name : brandNames.get(key) || p.perfil);
    });
    return out;
}

/* ---------- Comparativa ---------- */

/**
 * Indicadores por grupo (red, cliente o perfil) desde los posts del periodo y el movimiento de
 * seguidores. Incluye promedios por post, mediana y dispersión de interacciones, participación
 * y un índice compuesto (promedio de z-scores de tasa, interacciones por post y crecimiento).
 */
export function comparePerformance(posts, followerRows, groupOf) {
    const groups = new Map();
    const ensure = (name) => {
        if (!groups.has(name)) {
            groups.set(name, { name, posts: [], profiles: new Set(), Nuevos: 0, Perdidos: 0, Neto: 0, startFollowers: 0, endFollowers: 0 });
        }
        return groups.get(name);
    };
    posts.forEach((row) => {
        const g = ensure(groupOf(row));
        g.posts.push(row);
        g.profiles.add(profileKey(row));
    });
    followerRows.forEach((row) => {
        const g = ensure(groupOf(row));
        g.profiles.add(profileKey(row));
        g.Nuevos += num(row.Nuevos);
        g.Perdidos += num(row.Perdidos);
        g.Neto += num(row.Neto);
        if (row.startValue) g.startFollowers += row.startValue;
        if (row.endValue) g.endFollowers += row.endValue;
    });
    const totalInteractions = posts.reduce((acc, r) => acc + num(r.Interacciones), 0);
    const list = Array.from(groups.values()).map((g) => {
        const interactions = g.posts.map(r => num(r.Interacciones));
        const sum = (field) => g.posts.reduce((acc, r) => acc + num(r[field]), 0);
        let base = 0;
        let baseInteractions = 0;
        g.posts.forEach((r) => {
            const denom = num(r.Alcance) > 0 ? num(r.Alcance) : num(r.Impresiones);
            if (denom > 0) { base += denom; baseInteractions += num(r.Interacciones); }
        });
        const stats = describe(interactions);
        const count = g.posts.length;
        return {
            name: g.name,
            profiles: g.profiles.size,
            posts: count,
            Alcance: sum('Alcance'),
            Impresiones: sum('Impresiones'),
            Vistas: sum('Vistas'),
            Interacciones: sum('Interacciones'),
            rate: base ? baseInteractions / base : null,
            perPost: count ? sum('Interacciones') / count : null,
            reachPerPost: count ? sum('Alcance') / count : null,
            median: stats?.median ?? null,
            cv: stats?.cv ?? null,
            share: totalInteractions ? sum('Interacciones') / totalInteractions : null,
            Neto: g.Neto,
            growth: g.startFollowers ? (g.endFollowers - g.startFollowers) / g.startFollowers : null,
            rows: g.posts,
        };
    });
    /* Índice: z-score de cada indicador contra los demás grupos, promediado. */
    const keys = ['rate', 'perPost', 'growth'];
    const zStats = Object.fromEntries(keys.map((k) => {
        const vals = list.map(i => i[k]).filter(Number.isFinite);
        return [k, { m: mean(vals), sd: stdDev(vals) }];
    }));
    list.forEach((item) => {
        const zs = keys
            .filter(k => Number.isFinite(item[k]) && zStats[k].sd)
            .map(k => (item[k] - zStats[k].m) / zStats[k].sd);
        item.score = zs.length ? mean(zs) : null;
    });
    return list;
}

/* ---------- Matriz de posicionamiento ---------- */

/** Cuadrante de un punto contra las medianas: estrella, nicho, masivo o rezagado. */
export function quadrantOf(x, y, medianX, medianY) {
    if (x >= medianX && y >= medianY) return 'Estrella';
    if (x < medianX && y >= medianY) return 'Nicho';
    if (x >= medianX && y < medianY) return 'Masivo';
    return 'Rezagado';
}

/* ---------- Día y hora ---------- */

export const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

/** Fecha local de «2026-10-14 8:01:22» o «2026-10-14T08:01:22». */
export function parsePostDate(value) {
    const text = String(value || '').trim().replace(' ', 'T');
    const match = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{1,2}):(\d{2}))?/);
    if (!match) return null;
    const [, y, m, d, h = '0', mi = '0'] = match;
    return new Date(Number(y), Number(m) - 1, Number(d), Number(h), Number(mi));
}

/**
 * Mapa de calor día de la semana × franja horaria: posts y mediana de la métrica.
 * @returns {{cells: Object[][], slots: string[]}} cells[día][franja] = {count, median, mean}
 */
export function weekdayHourHeatmap(posts, metric = 'Interacciones', slotHours = 3) {
    const slots = [];
    for (let h = 0; h < 24; h += slotHours) slots.push(`${String(h).padStart(2, '0')}–${String(Math.min(h + slotHours, 24)).padStart(2, '0')}`);
    const buckets = WEEKDAYS.map(() => slots.map(() => []));
    posts.forEach((row) => {
        const date = parsePostDate(row['Fecha Publicación']);
        if (!date) return;
        const day = (date.getDay() + 6) % 7;
        const slot = Math.floor(date.getHours() / slotHours);
        buckets[day][slot].push(num(row[metric]));
    });
    const cells = buckets.map(day => day.map((values) => {
        const sorted = [...values].sort((a, b) => a - b);
        return { count: values.length, median: quantile(sorted, 0.5), mean: mean(values) };
    }));
    return { cells, slots };
}

/* ---------- Palabras ---------- */

export const STOPWORDS = new Set((
    'a al algo algun alguna algunas alguno algunos ante antes aqui asi aun aunque bajo bien cada casi como con contra cual cuales cuando cuanto de del desde donde dos e el ella ellas ello ellos en entre era eran es esa esas ese eso esos esta estaba estado estan estar estas este esto estos fue fueron ha hace hacer han hasta hay la las le les lo los mas me mi mis mucho muy nada ni no nos nuestra nuestro nuestros o os otra otro otros para pero poco por porque que quien quienes se sea segun ser si sido sin sobre solo son su sus tambien tan tanto te tener tiene tienen todo todos tu tus un una uno unos usted ustedes y ya yo the and for you your with this that are our from will have more about its it is in on of to be at by an or as we can all new mas hoy aqui conoce descubre sus nuestro nuestra cada vez dia dias asi entre ademas ver mejor gran parte puede pueden forma traves tus te ti link hashtag click clic sobre'
).split(/\s+/));

/**
 * Frecuencia de palabras o hashtags en los textos, con interacciones promedio de los posts
 * que la usan. Ignora enlaces, menciones, números y palabras vacías.
 * @returns {Array<{word, count, posts, interactions, avgInteractions}>}
 */
export function wordFrequencies(posts, { mode = 'words', minLength = 4, limit = 80, extraStopwords = [] } = {}) {
    const extra = new Set(extraStopwords.map(w => normalizeText(w)));
    const map = new Map();
    posts.forEach((row) => {
        const text = String(row.Texto || '');
        if (!text || text === '(Sin dato)') return;
        /* LinkedIn exporta los hashtags como «hashtag#Salud». */
        const cleaned = text
            .replace(/hashtag#/gi, '#')
            .replace(/https?:\/\/\S+/g, ' ')
            .replace(/www\.\S+/g, ' ');
        const tokens = mode === 'hashtags'
            ? (cleaned.match(/#[\p{L}\p{N}_]+/gu) || []).map(t => t.toLowerCase())
            : (cleaned.replace(/[#@][\p{L}\p{N}_]+/gu, ' ').match(/[\p{L}]+/gu) || [])
                .map(t => t.toLowerCase())
                .filter(t => t.length >= minLength && !STOPWORDS.has(normalizeText(t)) && !extra.has(normalizeText(t)));
        const seen = new Set();
        tokens.forEach((token) => {
            const key = mode === 'hashtags' ? normalizeText(token) : normalizeText(token);
            if (!map.has(key)) map.set(key, { word: token, count: 0, posts: 0, interactions: 0 });
            const item = map.get(key);
            item.count += 1;
            if (!seen.has(key)) {
                seen.add(key);
                item.posts += 1;
                item.interactions += num(row.Interacciones);
            }
        });
    });
    return Array.from(map.values())
        .map(item => ({ ...item, avgInteractions: item.posts ? item.interactions / item.posts : 0 }))
        .sort((a, b) => b.count - a.count || b.interactions - a.interactions)
        .slice(0, limit);
}

/**
 * Publicaciones por día de la semana y por hora, separadas por red. Con `measure = 'median'`
 * cada punto es la mediana de la métrica de esos posts en vez de la cantidad.
 * @returns {{byDay: Object[], byHour: Object[], networks: string[]}}
 */
export function postingSchedule(posts, { measure = 'count', scoreOf = row => num(row.Interacciones) } = {}) {
    const networks = new Set();
    const days = WEEKDAYS.map(() => new Map());
    const hours = Array.from({ length: 24 }, () => new Map());
    posts.forEach((row) => {
        const date = parsePostDate(row['Fecha Publicación']);
        if (!date) return;
        networks.add(row.Red);
        const push = (bucket) => {
            if (!bucket.has(row.Red)) bucket.set(row.Red, []);
            bucket.get(row.Red).push(scoreOf(row));
        };
        push(days[(date.getDay() + 6) % 7]);
        push(hours[date.getHours()]);
    });
    const reduce = (bucket, label) => {
        const point = { label };
        bucket.forEach((values, red) => {
            const sorted = [...values].sort((a, b) => a - b);
            point[red] = measure === 'median' ? quantile(sorted, 0.5) : values.length;
        });
        return point;
    };
    return {
        byDay: days.map((bucket, i) => reduce(bucket, WEEKDAYS[i])),
        byHour: hours.map((bucket, h) => reduce(bucket, `${String(h).padStart(2, '0')}h`)),
        networks: Array.from(networks),
    };
}
